/**
 * Cliente HTTP da API.
 *
 * Tudo é fetch RELATIVO (`/api/...`): em dev o proxy do Vite manda para a
 * :3400, em produção o nginx faz o proxy. Mesma origem nos dois casos, então o
 * cookie httpOnly de sessão viaja sozinho e não existe CORS.
 *
 * NÃO existe `VITE_API_URL` de propósito — variável do Vite é build-time, e
 * apontá-la para localhost por engano faria o Chrome mostrar o prompt de
 * Private Network Access no site publicado.
 */

/** Erro com o envelope da API já desempacotado. */
export class ErroApi extends Error {
  constructor(
    readonly status: number,
    readonly codigo: string,
    mensagem: string,
    /** Erros por campo, quando o backend devolve 422. */
    readonly campos?: Array<{ campo: string; mensagem: string }>,
  ) {
    super(mensagem)
    this.name = 'ErroApi'
  }

  /** Status 0 = a requisição não chegou (offline, DNS, servidor caído). */
  get eDeRede(): boolean {
    return this.status === 0
  }
  get eNaoAutorizado(): boolean {
    return this.status === 401
  }
  get eDeValidacao(): boolean {
    return this.status === 422 || this.status === 400
  }
}

/** Mensagem de último recurso, quando a API não manda uma. */
const MENSAGENS: Record<number, string> = {
  0: 'Sem conexão. Verifique sua internet e tente novamente.',
  401: 'Sua sessão expirou. Entre novamente.',
  403: 'Você não tem permissão para isso.',
  404: 'Não encontramos o que você procura.',
  409: 'Esse registro já existe.',
  413: 'Arquivo grande demais.',
  422: 'Confira os campos destacados.',
  429: 'Muitas tentativas. Aguarde um instante.',
  500: 'Erro no servidor. Tente novamente em instantes.',
  503: 'Serviço temporariamente indisponível.',
}

type Opcoes = Omit<RequestInit, 'body'> & { body?: unknown }

export async function apiFetch<T>(caminho: string, opcoes: Opcoes = {}): Promise<T> {
  const { body, headers, method = 'GET', ...resto } = opcoes
  const ehFormData = body instanceof FormData

  let resposta: Response
  try {
    resposta = await fetch(`/api${caminho}`, {
      ...resto,
      method,
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        // FormData tem de definir o próprio Content-Type (com o boundary).
        ...(body !== undefined && !ehFormData ? { 'Content-Type': 'application/json' } : {}),
        ...headers,
      },
      body: ehFormData ? body : body !== undefined ? JSON.stringify(body) : undefined,
    })
  } catch {
    // `fetch` só rejeita por falha de rede; erro HTTP vem como resposta.
    throw new ErroApi(0, 'rede', MENSAGENS[0]!)
  }

  if (resposta.status === 204) return undefined as T

  const texto = await resposta.text()
  const dados = texto ? tentarJson(texto) : null

  if (!resposta.ok) {
    const envelope = (dados as { erro?: { codigo?: string; mensagem?: string; campos?: never } })
      ?.erro
    throw new ErroApi(
      resposta.status,
      envelope?.codigo ?? 'erro_desconhecido',
      envelope?.mensagem ?? MENSAGENS[resposta.status] ?? 'Algo deu errado.',
      envelope?.campos,
    )
  }

  return dados as T
}

function tentarJson(texto: string): unknown {
  try {
    return JSON.parse(texto)
  } catch {
    return null
  }
}
